import { useCallback, useEffect, useRef, useState } from "react";
import { RuffleAdapter } from "./RuffleAdapter";
import type { PlayerState, RuntimeAdapter, SwfEvent } from "./swfTypes";

interface UseSwfPlayerOptions {
  swfUrl: string | null;
  autoplay?: boolean;
  onEvent?: (event: SwfEvent) => void;
  onComplete?: () => void;
  onError?: (error: Error) => void;
}

/**
 * Owns the player lifecycle and state machine. Depends only on the
 * RuntimeAdapter interface, so swapping RuffleAdapter for a future
 * native-HTML5 adapter requires no change here or in <SwfPlayer />.
 */
export function useSwfPlayer({
  swfUrl,
  autoplay = true,
  onEvent,
  onComplete,
  onError,
}: UseSwfPlayerOptions) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const adapterRef = useRef<RuntimeAdapter | null>(null);
  const [state, setState] = useState<PlayerState>("IDLE");
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  useEffect(() => {
    if (!swfUrl || !containerRef.current) return;
    let cancelled = false;

    const adapter: RuntimeAdapter = new RuffleAdapter();
    adapterRef.current = adapter;

    adapter.onEvent((event) => {
      onEvent?.(event);
      if (event.type === "COMPLETED") {
        setState("COMPLETED");
        onComplete?.();
      }
    });
    adapter.onError((err) => {
      if (cancelled) return;
      setState("ERROR");
      setErrorMessage(err.message);
      onError?.(err);
    });

    setState("LOADING");
    (async () => {
      try {
        await adapter.init();
        if (cancelled || !containerRef.current) return;
        const rect = containerRef.current.getBoundingClientRect();
        await adapter.load(containerRef.current, {
          url: swfUrl,
          width: rect.width,
          height: rect.height,
          scaleMode: "contain",
          autoplay,
        });
        if (cancelled) return;
        setState(autoplay ? "PLAYING" : "LOADED");
      } catch (err) {
        if (cancelled) return;
        const e =
          err instanceof Error ? err : new Error("Unknown SWF load error");
        setState("ERROR");
        setErrorMessage(e.message);
        onError?.(e);
      }
    })();

    return () => {
      cancelled = true;
      adapter.destroy();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [swfUrl]);

  const play = useCallback(() => {
    adapterRef.current?.play();
    setState("PLAYING");
  }, []);

  const pause = useCallback(() => {
    adapterRef.current?.pause();
    setState("PAUSED");
  }, []);

  const enterFullscreen = useCallback(() => {
    adapterRef.current?.requestFullscreen();
  }, []);

  useEffect(() => {
    const handleResize = () => {
      if (!containerRef.current || !adapterRef.current) return;
      const rect = containerRef.current.getBoundingClientRect();
      adapterRef.current.resize(rect.width, rect.height);
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  return { containerRef, state, errorMessage, play, pause, enterFullscreen };
}
